import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, Building2, Clock, CalendarDays,
  Banknote, Receipt, Target, UserPlus, GraduationCap,
  MessageSquare, BarChart3, Shield, Settings, UserCog,
  LogOut, X, ChevronRight, Megaphone, HeartPulse, ShieldAlert, FileText,
  CreditCard,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { navForRole, ROLE_META } from '../../utils/roles';
import { getAvatarUrl } from '../../utils/api';

const ICONS = {
  LayoutDashboard, Users, Building2, Clock, CalendarDays,
  Banknote, Receipt, Target, UserPlus, GraduationCap,
  MessageSquare, BarChart3, Shield, Settings, UserCog, Megaphone,
  HeartPulse, ShieldAlert, FileText, CreditCard, LogOut
};

function NavItem({ item, onClose, depth = 0 }) {
  const [isOpen, setIsOpen] = React.useState(true);
  const Icon = ICONS[item.icon];
  const hasChildren = item.children && item.children.length > 0;

  if (hasChildren) {
    return (
      <div style={{ marginBottom: 2 }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', padding: '9px 12px', borderRadius: 8,
            fontSize: 13.5, color: 'var(--sidebar-text)',
            background: 'transparent', border: 'none', cursor: 'pointer',
            transition: 'all 0.12s ease', textAlign: 'left',
          }}
          onMouseOver={e => e.currentTarget.style.background = 'var(--sidebar-hover)'}
          onMouseOut={e => e.currentTarget.style.background = 'transparent'}
        >
          {Icon && <Icon size={15} style={{ opacity: 0.92, flexShrink: 0 }} />}
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.label}
          </span>
          <ChevronRight size={13} style={{ opacity: 0.5, flexShrink: 0, transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>
        {isOpen && (
          <div style={{ marginLeft: 12, marginTop: 2, borderLeft: '1px solid var(--sidebar-border)' }}>
            {item.children.map(child => (
              <NavItem key={child.label} item={child} onClose={onClose} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.href}
      onClick={onClose}
      style={({ isActive }) => ({
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 12px', borderRadius: 8, marginBottom: 2,
        fontSize: depth > 0 ? 12.5 : 13.5, fontWeight: isActive ? 700 : 600,
        color: isActive ? 'var(--sidebar-active)' : 'var(--sidebar-text)',
        background: isActive ? 'var(--sidebar-active-bg)' : 'transparent',
        textDecoration: 'none', transition: 'all 0.12s ease',
        position: 'relative',
        marginLeft: depth > 0 ? 12 : 0,
      })}
      onMouseOver={e => {
        if (!e.currentTarget.classList.contains('active')) {
          e.currentTarget.style.background = 'var(--sidebar-hover)';
        }
      }}
      onMouseOut={e => {
        if (!e.currentTarget.getAttribute('aria-current')) {
          e.currentTarget.style.background = 'transparent';
        }
      }}
    >
      {({ isActive }) => (
        <>
          {isActive && depth === 0 && (
            <div style={{
              position: 'absolute', left: 0, top: '50%',
              transform: 'translateY(-50%)',
              width: 3, height: 16, borderRadius: '0 3px 3px 0',
              background: 'var(--sidebar-active)',
            }} />
          )}
          {Icon && <Icon size={15} style={{ opacity: isActive ? 1 : 0.92, flexShrink: 0 }} />}
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.label}
          </span>
          {isActive && <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--sidebar-active)', opacity: 0.8 }} />}
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const navItems = navForRole(user.role, user);
  const meta = ROLE_META[user.role] || { label: user.role, color: '#051B26' };
  const initials = user.full_name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() || '?';

  return (
    <>
      <div
        className={`sidebar-backdrop ${open ? 'show' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside className={`app-sidebar ${open ? 'open' : ''}`}>
        <div style={{
          padding: '12px 20px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--sidebar-border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img 
              src="/logo.png" 
              alt="Microhaem Logo" 
              style={{ height: '32px', width: 'auto', objectFit: 'contain' }} 
            />
          </div>
          <button className="btn btn-icon" onClick={onClose} style={{ display: 'none', color: 'var(--sidebar-muted)', background: 'none', border: 'none' }} aria-label="Close menu">
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--sidebar-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {user.profile_photo_url ? (
              <img 
                src={getAvatarUrl(user)} 
                alt="" 
                onError={(evt) => {
                  evt.target.style.display = 'none';
                  if (evt.target.nextSibling) evt.target.nextSibling.style.display = 'flex';
                }}
                style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, objectFit: 'cover', border: `1px solid ${meta.color}44` }}
              />
            ) : null}
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: `${meta.color}28`, border: `1px solid ${meta.color}44`,
              display: user.profile_photo_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, color: meta.color,
            }}>
              {initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#051B26', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.full_name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--sidebar-muted)', marginTop: 1 }}>
                {meta.label}
              </div>
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, overflowY: 'auto', padding: '10px 10px' }}>
          {navItems.map(item => (
            <NavItem key={item.label} item={item} onClose={onClose} />
          ))}
        </nav>

        <div style={{ padding: '10px', borderTop: '1px solid var(--sidebar-border)' }}>
          {['system_admin', 'hr_director', 'hr_manager', 'hr_officer', 'payroll_officer'].includes(user.role) && (
            <a
              href="https://hrm.mhsug.co.ug"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '9px 12px', borderRadius: 8,
                fontSize: 13.5, color: '#051B26', textDecoration: 'none',
                background: 'none', border: 'none', cursor: 'pointer', transition: 'all 0.12s', marginBottom: 4
              }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(137, 207, 240,0.08)'}
              onMouseOut={e => e.currentTarget.style.background = 'transparent'}
            >
              <Building2 size={15} style={{ opacity: 0.8 }} />
              HR Admin Portal
            </a>
          )}
          <button
            onClick={logout}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', padding: '9px 12px', borderRadius: 8,
              fontSize: 13.5, color: '#C53030',
              background: 'none', border: 'none', cursor: 'pointer', transition: 'all 0.12s',
            }}
            onMouseOver={e => e.currentTarget.style.background = 'rgba(220,38,38,0.08)'}
            onMouseOut={e => e.currentTarget.style.background = 'transparent'}
          >
            <LogOut size={15} style={{ opacity: 0.8 }} />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
